import { percentOf, roundQty } from './money';

export const ORDER_STATUSES = ['open', 'confirmed', 'in_progress', 'ready', 'shipped', 'completed', 'canceled'] as const;
export type OrderStatusKey = (typeof ORDER_STATUSES)[number];

export const ORDER_STATUS_META: Record<OrderStatusKey, { label: string; short: string; tone: 'info' | 'warning' | 'success' | 'neutral' | 'danger' | 'primary' }> = {
  open: { label: 'Novo pedido', short: 'Novo', tone: 'warning' },
  confirmed: { label: 'Confirmado', short: 'Confirmado', tone: 'info' },
  in_progress: { label: 'Em preparação', short: 'Preparando', tone: 'info' },
  ready: { label: 'Pronto para retirada', short: 'Pronto', tone: 'primary' },
  shipped: { label: 'Saiu para entrega', short: 'Enviado', tone: 'primary' },
  completed: { label: 'Concluído', short: 'Concluído', tone: 'success' },
  canceled: { label: 'Cancelado', short: 'Cancelado', tone: 'danger' },
};

/** Transições permitidas no fluxo de pedidos. */
export const ORDER_TRANSITIONS: Record<OrderStatusKey, OrderStatusKey[]> = {
  open: ['confirmed', 'in_progress', 'ready', 'shipped', 'completed', 'canceled'],
  confirmed: ['in_progress', 'ready', 'shipped', 'completed', 'canceled'],
  in_progress: ['ready', 'shipped', 'completed', 'canceled'],
  ready: ['shipped', 'completed', 'canceled'],
  shipped: ['completed', 'canceled'],
  completed: ['canceled'],
  canceled: [],
};

export function canTransition(from: OrderStatusKey, to: OrderStatusKey): boolean {
  return ORDER_TRANSITIONS[from].includes(to);
}

/** Próximo passo sugerido (botão principal na tela do pedido). */
export function nextOrderStep(status: OrderStatusKey, deliveryType: DeliveryTypeKey): OrderStatusKey | null {
  switch (status) {
    case 'open':
      return 'confirmed';
    case 'confirmed':
      return 'in_progress';
    case 'in_progress':
      return deliveryType === 'delivery' || deliveryType === 'shipping' ? 'shipped' : 'ready';
    case 'ready':
    case 'shipped':
      return 'completed';
    default:
      return null;
  }
}

export const PAYMENT_STATUS_META: Record<'unpaid' | 'partial' | 'paid' | 'refunded', { label: string; tone: 'warning' | 'info' | 'success' | 'neutral' }> = {
  unpaid: { label: 'A receber', tone: 'warning' },
  partial: { label: 'Pago parcialmente', tone: 'info' },
  paid: { label: 'Pago', tone: 'success' },
  refunded: { label: 'Estornado', tone: 'neutral' },
};

export const ORDER_SOURCE_LABELS: Record<'pos' | 'catalog' | 'manual' | 'api', string> = {
  pos: 'PDV',
  catalog: 'Catálogo online',
  manual: 'Pedido manual',
  api: 'API',
};

export const DELIVERY_TYPES = ['none', 'pickup', 'delivery', 'shipping'] as const;
export type DeliveryTypeKey = (typeof DELIVERY_TYPES)[number];
export const DELIVERY_TYPE_LABELS: Record<DeliveryTypeKey, string> = {
  none: 'Sem entrega (retirado na hora)',
  pickup: 'Retirada na loja',
  delivery: 'Entrega local',
  shipping: 'Envio (Correios/transportadora)',
};

// ----------------------------------------------------------------------------
// Cálculo de totais — usado IGUALMENTE no PDV (front) e na API (fonte da verdade)
// ----------------------------------------------------------------------------

export interface TotalsLine {
  quantity: number;
  listPriceCents: number;
  unitPriceCents: number;
  unitCostCents?: number | null;
}

export type OrderDiscountInput =
  | { type: 'percent'; value: number }
  | { type: 'fixed'; value: number }
  | null
  | undefined;

export interface TotalsInput {
  lines: TotalsLine[];
  discount?: OrderDiscountInput;
  couponDiscountCents?: number;
  deliveryFeeCents?: number;
  surchargePercent?: number;
}

export interface Totals {
  /** Soma dos itens a preço de tabela */
  grossCents: number;
  /** Soma dos descontos por item (preço de tabela - preço praticado) */
  itemsDiscountCents: number;
  /** Soma dos itens a preço praticado */
  subtotalCents: number;
  /** Desconto no pedido (manual) */
  discountCents: number;
  couponDiscountCents: number;
  deliveryFeeCents: number;
  surchargeCents: number;
  totalCents: number;
  costTotalCents: number;
  itemsCount: number;
}

export function lineTotalCents(quantity: number, unitPriceCents: number): number {
  return Math.round(roundQty(quantity) * unitPriceCents);
}

export function computeTotals(input: TotalsInput): Totals {
  let gross = 0;
  let subtotal = 0;
  let cost = 0;
  let count = 0;
  for (const l of input.lines) {
    gross += lineTotalCents(l.quantity, l.listPriceCents);
    subtotal += lineTotalCents(l.quantity, l.unitPriceCents);
    cost += lineTotalCents(l.quantity, l.unitCostCents ?? 0);
    count += l.quantity;
  }
  let discount = 0;
  if (input.discount) {
    discount =
      input.discount.type === 'percent'
        ? percentOf(subtotal, Math.min(100, Math.max(0, input.discount.value)))
        : Math.max(0, Math.round(input.discount.value));
  }
  discount = Math.min(discount, subtotal);
  const coupon = Math.min(Math.max(0, input.couponDiscountCents ?? 0), subtotal - discount);
  const delivery = Math.max(0, input.deliveryFeeCents ?? 0);
  const afterDiscounts = subtotal - discount - coupon;
  const surcharge = input.surchargePercent ? percentOf(afterDiscounts, input.surchargePercent) : 0;
  return {
    grossCents: gross,
    itemsDiscountCents: Math.max(0, gross - subtotal),
    subtotalCents: subtotal,
    discountCents: discount,
    couponDiscountCents: coupon,
    deliveryFeeCents: delivery,
    surchargeCents: surcharge,
    totalCents: Math.max(0, afterDiscounts + surcharge + delivery),
    costTotalCents: cost,
    itemsCount: roundQty(count),
  };
}

/** Status de pagamento a partir do total e do valor pago. */
export function paymentStatusFor(totalCents: number, paidCents: number): 'unpaid' | 'partial' | 'paid' {
  if (paidCents <= 0) return totalCents === 0 ? 'paid' : 'unpaid';
  return paidCents >= totalCents ? 'paid' : 'partial';
}

/** Taxa de maquininha/meio de pagamento em centavos. */
export function paymentFeeCents(amountCents: number, feePercent = 0, feeFixedCents = 0): number {
  return Math.round((amountCents * feePercent) / 100) + (amountCents > 0 ? feeFixedCents : 0);
}

/** Formata o número do pedido: 42 -> "#0042" */
export function formatOrderNumber(n: number): string {
  return `#${String(n).padStart(4, '0')}`;
}
